import { useMemo } from 'react';

import { ApproveListData } from '@Components/MainForm/components/ApproveList/types';
import { ItRequisitionTypeCodes } from '@Enum/api/ItRequisitionTypeCodes.enum';
import { ITRequestFormResponse } from '@Types/api/form';

import { OptionNum, Option } from '@Components/types/options';
import { TypeDict } from '@Types/index';
import { Organization } from '@Types/api/common/dictionary';

import { ITRequestFormValues } from './type';
import { userTypes } from '../isNewUser.enum';
import {
  accountModuleOptions,
  moduleOptions,
  requiredRoleOptions,
} from '../fieldsOptions';
import { DEFAULT_REPLACEMENT_FEE, DEFAULT_CURRENCY } from './constants';

export const useDataSource = (
  formData: ITRequestFormResponse,
  formikValues: ITRequestFormValues,
  documentId: string,
  itReqTypeOptions: OptionNum[],
  simCapValues: TypeDict[],
  organizations: Organization[] | undefined,
  projects: Option[],
  itAccessories: Option[]
): ApproveListData => {
  const dataSource = useMemo(() => {
    const reqItemDesc =
      itReqTypeOptions.find((item) => item.value === formikValues.itReqType)
        ?.label || '';

    const output: ApproveListData = [
      {
        label: 'Requisition Item',
        value: reqItemDesc,
      },
    ];

    if (formikValues.itReqType === ItRequisitionTypeCodes.NEW_SIM_CARD) {
      const simCapDesc =
        simCapValues.find(
          (item) => item.codeNum === formikValues.simCardCreditLimit
        )?.codeDesc || '';

      const fields = [
        {
          label: 'SIM Card Credit Limit',
          value: simCapDesc,
        },
        {
          label: 'Contact No.',
          value: formikValues.contactNo,
        },
        {
          label: 'Remarks',
          value: formikValues.remarks,
        },
      ];
      return output.concat(fields);
    }

    if (formikValues.itReqType === ItRequisitionTypeCodes.EMAIL) {
      const documentEl = documentId
        ? {
            label: 'Documents',
            value: formikValues.document,
            extra: {
              document: {
                documentId,
              },
            },
          }
        : null;

      const fields = [
        {
          label: 'Email ID',
          value: formData.username,
        },
        {
          label: 'Email Signature',
          value: formData.accessories,
        },
        documentEl,
        {
          label: 'Contact No.',
          value: formikValues.contactNo,
        },
        {
          label: 'Remarks',
          value: formikValues.remarks,
        },
      ].filter((item) => item) as ApproveListData;
      return output.concat(fields);
    }

    if (formikValues.itReqType === ItRequisitionTypeCodes.BUILD_SMART_ACCESS) {
      const userInformationDesc = userTypes[formikValues.isNewUser];
      const moduleDesc = formikValues.module
        .map(
          (moduleCode) =>
            moduleOptions.find((item) => item.value === moduleCode)?.label || ''
        )
        .join(', ');

      const accountsModuleDesc = formikValues.accountsModule
        .map(
          (moduleCode) =>
            accountModuleOptions.find((item) => item.value === moduleCode)
              ?.label || ''
        )
        .join(', ');

      const projectsDesc = formikValues.projects
        .map(
          (projectCode) =>
            projects.find((item) => item.value === projectCode)?.label || ''
        )
        .join(', ');

      const ogranizationDesc =
        organizations?.find(
          (item) => String(item.borgId) === formikValues.organization
        )?.borgName || '';

      const requiredRoleDesc =
        requiredRoleOptions.find(
          (item) => item.value === formikValues.requiredRole
        )?.label || '';

      const fields = [
        {
          label: 'User Information',
          value: userInformationDesc,
        },
        {
          label: 'Module',
          value: moduleDesc,
        },
        ...(moduleDesc === 'Accounts'
          ? [
              {
                label: 'Accounts Module',
                value: accountsModuleDesc,
              },
            ]
          : []),
        {
          label: 'Organization',
          value: ogranizationDesc,
        },
        {
          label: 'Projects',
          value: projectsDesc,
        },
        {
          label: 'Required Role',
          value: requiredRoleDesc,
        },
        {
          label: 'Contact No.',
          value: formikValues.contactNo,
        },
        {
          label: 'Remarks',
          value: formikValues.remarks,
        },
      ].filter((item) => item);
      return output.concat(fields);
    }

    if (formikValues.itReqType === ItRequisitionTypeCodes.ACCESSORIES) {
      const accessoriesDesc = formikValues.accessories
        .map(
          (accessoryValue) =>
            itAccessories.find(
              (accessory) => accessory.value === accessoryValue.trim()
            )?.label
        )
        .join(', ');

      const fields = [
        {
          label: 'Accessories',
          value: accessoriesDesc,
        },
        {
          label: 'Contact No.',
          value: formikValues.contactNo,
        },
        {
          label: 'Remarks',
          value: formikValues.remarks,
        },
      ];
      return output.concat(fields);
    }

    if (formikValues.itReqType === ItRequisitionTypeCodes.INTERNATIONAL_CALLS) {
      const intCallsDesc = `${formikValues.intCallsNumber}`;

      const fields = [
        {
          label: 'International Calls Number',
          value: intCallsDesc,
        },
        {
          label: 'Remarks',
          value: formikValues.remarks,
        },
      ];
      return output.concat(fields);
    }

    if (formikValues.itReqType === ItRequisitionTypeCodes.ASSET_STATUS_CHANGE) {
      const fields = [
        {
          label: 'Contact No.',
          value: formikValues.contactNo,
        },
        {
          label: 'Remarks',
          value: formikValues.remarks,
        },
      ];
      return output.concat(fields);
    }

    if (formikValues.itReqType === ItRequisitionTypeCodes.LOST_SIM_CARD) {
      const fields = [
        {
          label: 'Mobile',
          value: formikValues.contactNo,
        },
        {
          label: 'Replacement Fee',
          value: `${DEFAULT_REPLACEMENT_FEE} ${DEFAULT_CURRENCY}`,
        },
        {
          label: 'Remarks',
          value: formikValues.remarks,
        },
      ];
      return output.concat(fields);
    }

    if (formikValues.itReqType === ItRequisitionTypeCodes.SIM_ADDON_REQUEST) {
      const fields = [
        {
          label: 'Mobile',
          value: formikValues.contactNo,
        },
        {
          label: 'Replacement Fee',
          value: `${DEFAULT_REPLACEMENT_FEE} ${DEFAULT_CURRENCY}`,
        },
        {
          label: 'Remarks',
          value: formikValues.remarks,
        },
      ];
      return output.concat(fields);
    }

    return output;
  }, [
    itReqTypeOptions,
    formikValues,
    simCapValues,
    documentId,
    formData.username,
    formData.accessories,
    projects,
    organizations,
    itAccessories,
  ]);

  return dataSource;
};
